import { FunctionComponent } from "preact";
import omitBy from "lodash/omitBy";
import isNil from "lodash/isNil";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "preact/hooks";
import styles from "../../styles/sidebar.scss?inline";
import SidebarButton from "../SidebarButton";
import { getHighestZIndex } from "../../services/htmlService";
import {
  PLUGILO_EXTENSION_CLOSE_SIDEBAR,
  PLUGILO_EXTENSION_OPEN_SIDEBAR_FULL,
  PLUGILO_EXTENSION_OPEN_SIDEBAR_FULL_CONFIRMED,
  PLUGILO_EXTENSION_RELOAD,
} from "../../constants/postMessageNames";

const Sidebar: FunctionComponent = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [sidebarFull, setSidebarFull] = useState(false);
  const [sidebarFrameInitiated, setSidebarInitiated] = useState(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [zIndex, setZIndex] = useState(0);
  const [refreshKey, setRefreshKey] = useState<string>();
  const [fallbackDomain, setFallbackDomain] = useState<string>();

  const updateSidebarIndex = useCallback(() => {
    const highestZIndex = getHighestZIndex();
    const newZIndex = highestZIndex + 1;
    const maxZIndex = 2147483647;
    setZIndex(Math.min(newZIndex, maxZIndex));
  }, []);

  const moveSidebarToLastBodyChildren = useCallback(() => {
    const element = document.getElementById("plugilo-widget-root");
    if (element && element.nextSibling) {
      document.body.appendChild(element);
    }
  }, []);

  useEffect(() => {
    if (sidebarVisible) {
      setSidebarInitiated(true);
      updateSidebarIndex();
    }
  }, [sidebarVisible]);

  useEffect(() => {
    updateSidebarIndex();
  }, []);

  useEffect(() => {
    window.addEventListener("message", (e) => {
      const { action } = e.data || {};

      switch (action) {
        case PLUGILO_EXTENSION_OPEN_SIDEBAR_FULL:
          setSidebarFull(true);
          break;
        case PLUGILO_EXTENSION_CLOSE_SIDEBAR:
          setSidebarFull(false);
          break;
        case PLUGILO_EXTENSION_RELOAD:
          setLoaded(false);
          setSidebarFull(false);
          setFallbackDomain(fallbackDomain);
          setRefreshKey(new Date().getTime().toString());
          break;
        default:
          break;
      }
    });
  }, []);

  useEffect(() => {
    if (sidebarFull) {
      const iframe = iframeRef.current;
      if (iframe) {
        iframe.contentWindow?.postMessage(
          {
            action: PLUGILO_EXTENSION_OPEN_SIDEBAR_FULL_CONFIRMED,
          },
          "*"
        );
      }
    }
  }, [sidebarFull]);

  const toggleSidebar = (): void => {
    if (!sidebarVisible) {
      moveSidebarToLastBodyChildren();
    }
    setTimeout(() => {
      setSidebarVisible((visible) => !visible);
    }, 0);
  };

  const onLoad = (): void => {
    setLoaded(true);
  };

  const activeDomain = location.host.startsWith("www.")
    ? location.host.replace("www.", "")
    : location.host;

  const queryParams = useMemo(() => {
    const params: {} = {
      ad: activeDomain,
      url: location.href,
      fallbackDomain,
    };

    return new URLSearchParams(omitBy(params, isNil)).toString();
  }, []);

  return (
    <>
      <style>{styles}</style>
      <div
        id="plugilo-widget-sidebar"
        className={`sidebar-wrapper ${sidebarVisible ? "visible" : ""} ${
          sidebarFull ? "full" : ""
        }`}
        style={{ zIndex }}
      >
        {!loaded && (
          <div className="sidebar-loading-container">
            {/* <img
            style={{
              display: "block",
            }}
            width={120}
            height={48}
            alt="Plugilo"
            src={"images/logo_plugilo_black.svg"}
          /> */}
          </div>
        )}
        {sidebarFrameInitiated && (
          <iframe
            className="sidebar-frame"
            src={`${
              import.meta.env.VITE_IFRAME_HOST
            }/ext-sidebar?${queryParams}`}
            onLoad={onLoad}
            ref={iframeRef}
            key={refreshKey}
          />
        )}
        <SidebarButton
          onClick={toggleSidebar}
          sidebarVisible={sidebarVisible}
        />
      </div>
    </>
  );
};

// Sidebar.propTypes = {
//   onClick: PropTypes.func,
// };

export default Sidebar;
